import React from 'react';
import { motion } from 'framer-motion';
import { CloudIcon } from 'lucide-react';
import { signInWithPopup } from 'firebase/auth';
import { auth, googleProvider, microsoftProvider } from '../../config/firebase';
import { useTranslation } from '../../hooks/useTranslation';

interface CloudSaveProps {
  onSuccess?: () => void;
}

export const CloudSave: React.FC<CloudSaveProps> = ({ onSuccess }) => {
  const { t } = useTranslation();

  const handleGoogleLogin = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
      onSuccess?.();
    } catch (error) {
      console.error('Google login error:', error);
    }
  };

  const handleMicrosoftLogin = async () => {
    try {
      await signInWithPopup(auth, microsoftProvider);
      onSuccess?.();
    } catch (error) {
      console.error('Microsoft login error:', error);
    }
  };

  return (
    <div className="bg-white/95 backdrop-blur-xl rounded-3xl border border-white/20 shadow-[0_8px_40px_rgba(0,0,0,0.12)] p-12 space-y-8 w-full max-w-md mx-auto">
      <div className="text-center space-y-3">
        <div className="w-16 h-16 rounded-[28px] bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center mx-auto mb-6 shadow-[0_0_20px_rgba(0,0,0,0.03)]">
          <CloudIcon className="w-8 h-8 text-primary-600" />
        </div>
        <h1 className="text-3xl font-semibold text-gray-900 tracking-tight">
          {t('auth.welcome')}
        </h1>
        <p className="text-gray-500 text-lg">
          {t('auth.signInToContinue')}
        </p>
      </div>

      <div className="space-y-4">
        <motion.button
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          onClick={handleGoogleLogin}
          className="group w-full flex items-center justify-center gap-3 px-6 py-4 border border-gray-200 rounded-2xl
                   text-gray-700 bg-white hover:bg-gray-50 transition-all duration-200
                   focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 shadow-[0_2px_8px_rgba(0,0,0,0.02)]"
        >
          <div className="w-5 h-5 rounded-full bg-white/70 backdrop-blur-sm flex items-center justify-center shadow-[3px_1px_8px_rgba(0,0,0,0.12)]">
            <img src="/google.svg" alt="Google" className="w-3.5 h-3.5 opacity-60 group-hover:opacity-80 transition-opacity duration-300" />
          </div>
          <span className="text-base font-medium">{t('auth.continueWithGoogle')}</span>
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          onClick={handleMicrosoftLogin}
          className="group w-full flex items-center justify-center gap-3 px-6 py-4 border border-gray-200 rounded-2xl
                   text-gray-700 bg-white hover:bg-gray-50 transition-all duration-200
                   focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 shadow-[0_2px_8px_rgba(0,0,0,0.02)]"
        >
          <div className="w-5 h-5 rounded-full bg-white/70 backdrop-blur-sm flex items-center justify-center shadow-[3px_1px_8px_rgba(0,0,0,0.12)]">
            <img src="/microsoft.svg" alt="Microsoft" className="w-3.5 h-3.5 opacity-60 group-hover:opacity-80 transition-opacity duration-300" />
          </div>
          <span className="text-base font-medium">{t('auth.continueWithMicrosoft')}</span>
        </motion.button>
      </div>

      <p className="text-center text-sm text-gray-500 max-w-sm mx-auto">
        {t('auth.privacyNotice')}
      </p>
    </div>
  );
}; 